import React, { useState, ReactNode, useRef, useEffect, useCallback } from 'react';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import colors from '@/theme/colors';
import { useIsMobile } from '@/hooks';

interface PersistentTooltipProps extends TooltipProps {
  title: ReactNode;
}

const tooltipStyles = {
  popper: {
    sx: {
      [`& .MuiTooltip-arrow`]: {
        color: colors.midnight,
      },
      [`& .MuiTooltip-tooltip`]: {
        backgroundColor: colors.midnight,
        fontSize: '14px',
        borderRadius: '5px',
        fontFamily: 'Montserrat Medium',
        lineHeight: '1.43',
        letterSpacing: '-0.4px',
      },
    },
  },
};

export const PersistentTooltip: React.FC<PersistentTooltipProps> = ({
  title,
  children,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const [mobilePlacement, setMobilePlacement] = useState<'top' | 'bottom'>('top');
  const elementRef = useRef<HTMLSpanElement>(null);

  const handleScroll = useCallback(() => {
    if (elementRef.current && isMobile) {
      const rect = elementRef.current.getBoundingClientRect();
      const middleOfScreen = window.innerHeight / 2;
      const placement = rect.top < middleOfScreen ? 'bottom' : 'top';
      setMobilePlacement(placement);
    }
  }, [isMobile]);

  useEffect(() => {
    const debouncedScrollHandler = () => {
      let timeoutId: ReturnType<typeof setTimeout>;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(handleScroll, 100);
      };
    };

    window.addEventListener('scroll', debouncedScrollHandler());
    handleScroll();

    return () => {
      window.removeEventListener('scroll', debouncedScrollHandler());
    };
  }, [handleScroll]);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleToggleTooltip = () => {
    setOpen(!open);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleTooltipClose();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <span onKeyDown={handleKeyDown}>
        <Tooltip
          title={title}
          open={open}
          arrow
          placement={isMobile ? mobilePlacement : 'right'}
          componentsProps={tooltipStyles}
          onClose={handleTooltipClose}
          enterTouchDelay={0}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          PopperProps={{
            disablePortal: true,
          }}
          {...rest}
        >
          <span
            ref={elementRef}
            onClick={handleToggleTooltip}
            aria-describedby="persistent-tooltip"
          >
            {children}
          </span>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
};
